import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ConnectionRouteInfo } from '../../types/nodeConfig';
import { formatRouteLabel } from '../../lib/routeLabels';

interface Props {
  routes: ConnectionRouteInfo[];
  value: string;
  direction: 'incoming' | 'outgoing';
  disabled?: boolean;
  placeholder?: string;
  onChange: (routeId: string) => void;
}

interface PanelPosition {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
}

const PANEL_MIN_WIDTH = 280;
const PANEL_PREFERRED_HEIGHT = 320;
const VIEWPORT_MARGIN = 8;

function computePanelPosition(trigger: HTMLButtonElement): PanelPosition {
  const rect = trigger.getBoundingClientRect();
  const width = Math.max(rect.width, PANEL_MIN_WIDTH);
  let left = rect.left;
  if (left + width > window.innerWidth - VIEWPORT_MARGIN) {
    left = Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN);
  }

  const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
  const spaceAbove = rect.top - VIEWPORT_MARGIN;
  const openBelow = spaceBelow >= 160 || spaceBelow >= spaceAbove;

  if (openBelow) {
    return {
      top: rect.bottom + 4,
      left,
      width,
      maxHeight: Math.max(160, Math.min(PANEL_PREFERRED_HEIGHT, spaceBelow - 4)),
    };
  }

  const maxHeight = Math.max(160, Math.min(PANEL_PREFERRED_HEIGHT, spaceAbove - 4));
  return {
    top: Math.max(VIEWPORT_MARGIN, rect.top - maxHeight - 4),
    left,
    width,
    maxHeight,
  };
}

export default function SearchableRouteSelect({
  routes,
  value,
  direction,
  disabled,
  placeholder = 'Select route…',
  onChange,
}: Props) {
  const listId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState<PanelPosition>({
    top: 0,
    left: 0,
    width: PANEL_MIN_WIDTH,
    maxHeight: PANEL_PREFERRED_HEIGHT,
  });

  const selected = routes.find((r) => r.route_id === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return routes;
    return routes.filter((r) => {
      const label = formatRouteLabel(r, direction).toLowerCase();
      return label.includes(q) || r.route_id.toLowerCase().includes(q);
    });
  }, [routes, query, direction]);

  const updatePanelPosition = () => {
    if (!triggerRef.current) return;
    setPanelStyle(computePanelPosition(triggerRef.current));
  };

  useLayoutEffect(() => {
    if (!open) return;
    updatePanelPosition();
  }, [open, filtered.length]);

  useEffect(() => {
    if (!open) return;

    const onReposition = () => updatePanelPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  if (routes.length === 0) {
    return (
      <input
        className="input-field w-full text-xs"
        disabled
        value="No routes connected"
        readOnly
      />
    );
  }

  const dropdown =
    open && !disabled
      ? createPortal(
          <>
            <button
              type="button"
              className="fixed inset-0 z-[220] cursor-default"
              aria-label="Close route picker"
              onClick={close}
            />
            <div
              id={listId}
              role="listbox"
              className="fixed z-[221] flex flex-col overflow-hidden rounded-lg border border-border bg-surface-raised shadow-xl"
              style={{
                top: panelStyle.top,
                left: panelStyle.left,
                width: panelStyle.width,
                maxHeight: panelStyle.maxHeight,
              }}
            >
              <input
                type="text"
                className="input-field shrink-0 rounded-b-none border-0 border-b border-border text-xs"
                placeholder="Search routes…"
                value={query}
                autoFocus
                onChange={(e) => setQuery(e.target.value)}
              />
              <ul className="min-h-0 flex-1 overflow-y-auto py-1">
                {filtered.length === 0 ? (
                  <li className="px-3 py-2 text-xs text-text-subtle">No matches</li>
                ) : (
                  filtered.map((r) => (
                    <li key={r.route_id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={r.route_id === value}
                        className={`w-full px-3 py-2 text-left text-xs leading-snug hover:bg-surface-overlay ${
                          r.route_id === value
                            ? 'bg-accent/15 text-text'
                            : 'text-text-muted'
                        }`}
                        onClick={() => {
                          onChange(r.route_id);
                          close();
                        }}
                      >
                        {formatRouteLabel(r, direction)}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </>,
          document.body,
        )
      : null;

  return (
    <div className="relative min-w-[12rem]">
      <button
        ref={triggerRef}
        type="button"
        className="input-field flex w-full min-w-[12rem] items-center justify-between text-left text-xs"
        disabled={disabled}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-haspopup="listbox"
        onClick={() => {
          if (disabled) return;
          setOpen((current) => !current);
        }}
      >
        <span className="truncate">
          {selected ? formatRouteLabel(selected, direction) : placeholder}
        </span>
        <span className="shrink-0 text-text-subtle">▾</span>
      </button>
      {dropdown}
    </div>
  );
}
